/**
 * StatCallout — Design-system primitive (re-homed from `components/v2/`).
 *
 * WAVE 6 BATCH 2: Canonical home is now `design-system/components/`.
 * The legacy `components/v2/StatCallout.jsx` is retained as a thin shim
 * that re-exports this module so that deferred consumers (V2Preview)
 * continue to compile until they are migrated in a later batch.
 *
 * A single-metric tile. Value is displayed large; label is a small caption.
 * Optional trend delta with directional arrow, optional icon, and semantic
 * variant (default / success / warning / info / danger) tints the chrome.
 *
 * Test IDs (UNCHANGED):
 *   v2-stat-callout, v2-stat-value, v2-stat-label, v2-stat-trend, v2-stat-icon
 */
import React from "react";
import clsx from "clsx";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

const VARIANT_STYLES = {
  default: {
    bar:  "bg-[var(--ds-color-accent)]",
    chip: "bg-[var(--ds-color-accent-soft)] text-[var(--ds-color-accent-strong)]",
  },
  success: {
    bar:  "bg-[var(--ds-color-success)]",
    chip: "bg-[var(--ds-color-success-soft)] text-[var(--ds-color-success)]",
  },
  warning: {
    bar:  "bg-[var(--ds-color-warning)]",
    chip: "bg-[var(--ds-color-warning-soft)] text-[var(--ds-color-warning)]",
  },
  info: {
    bar:  "bg-[var(--ds-color-info)]",
    chip: "bg-[var(--ds-color-info-soft)] text-[var(--ds-color-info)]",
  },
  danger: {
    bar:  "bg-[var(--ds-color-danger)]",
    chip: "bg-[var(--ds-color-danger-soft)] text-[var(--ds-color-danger)]",
  },
};

const TREND_ICON = { up: ArrowUpRight, down: ArrowDownRight, flat: Minus };

export default function StatCallout({
  value,
  label,
  trend,
  icon,
  variant = "default",
}) {
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.default;
  const TrendIcon = trend ? TREND_ICON[trend.direction] : null;
  return (
    <div
      data-testid="v2-stat-callout"
      className="relative overflow-hidden rounded-[var(--ds-radius-lg)] bg-[var(--ds-color-surface)] border border-[var(--ds-color-border)] shadow-[var(--ds-shadow-card)] p-5 md:p-6"
    >
      <div aria-hidden="true" className={clsx("absolute inset-y-0 left-0 w-1", styles.bar)} />
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
          {label}
        </span>
        {icon ? (
          <span
            data-testid="v2-stat-icon"
            className={clsx("rounded-[var(--ds-radius-sm)] p-1.5", styles.chip)}
          >
            {icon}
          </span>
        ) : null}
      </div>
      <div
        data-testid="v2-stat-value"
        className="text-[36px] leading-none tabular-nums text-[var(--ds-color-ink)]"
        style={{
          fontFamily: "var(--ds-font-display)",
          fontWeight: 600,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>
      <div
        data-testid="v2-stat-label"
        className="mt-2 text-[13px] text-[var(--ds-color-ink-muted)] sr-only"
      >
        {label}
      </div>
      {trend ? (
        <div
          data-testid="v2-stat-trend"
          className={clsx(
            "mt-4 inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-2 py-1 text-[11px] font-semibold",
            styles.chip
          )}
        >
          {TrendIcon ? <TrendIcon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
          <span>{trend.delta}</span>
        </div>
      ) : null}
    </div>
  );
}
